import React, { useState } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { useOutletContext } from "react-router-dom";
import axiosInstant from "../../../api";
import { getBrand } from "../../../api/endpoints";
import { IBrandDetailsContext } from "../SingleBrandDetails";
import { ProductBrandList } from "../../admin/productsV2/productModal/BrandList";

// Internal-admin editor for the brands associated with a network (RMN).
// Moved out of the Details form; saves via PATCH /brands/:id { brand_ids }
// (the API only reconciles associations when brand_ids is present).
export function NetworkBrandsPanel() {
    const { brand, setBrand } = useOutletContext<IBrandDetailsContext>();
    const [networkBrands, setNetworkBrands] = useState<
        Array<{ id: number; name: string }>
    >(brand.associated_brands || []);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                name: brand.name,
                brand_ids: networkBrands.map((b) => b.id),
            };
            const response = await axiosInstant.patch(
                getBrand(brand.id as number),
                payload
            );
            if (response?.data) setBrand(response.data);
            toast.success("Network brands saved.");
        } catch (error) {
            toast.error("Failed to save network brands.");
        }
        setSaving(false);
    };

    return (
        <Stack spacing={3} className="max-w-[480px]">
            <Box>
                <Typography variant="h6" gutterBottom>
                    Network Brands
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    Brands associated with this network. They appear as brand
                    options inside the network&apos;s white-label portal tools.
                </Typography>
            </Box>

            <ProductBrandList
                onAddition={(added) => {
                    // The autocomplete's IBrand types id as string, but the
                    // API returns numeric ids — normalize before comparing.
                    const addedId = Number(added.id);
                    if (!addedId || addedId === brand.id) return;
                    if (!networkBrands.find((b) => b.id === addedId)) {
                        setNetworkBrands((prev) => [
                            ...prev,
                            { id: addedId, name: added.name },
                        ]);
                    }
                }}
            />

            {networkBrands.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {networkBrands.map((b) => (
                        <Chip
                            key={b.id}
                            label={b.name}
                            onDelete={() =>
                                setNetworkBrands(
                                    networkBrands.filter((x) => x.id !== b.id)
                                )
                            }
                        />
                    ))}
                </Stack>
            ) : (
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                    No brands in this network yet.
                </Typography>
            )}

            <Box>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving…" : "Save network brands"}
                </Button>
            </Box>
        </Stack>
    );
}
